import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import cn from "classnames";
import { t } from "i18next";
import { useState } from "react";
import { Chart } from "react-chartjs-2";

import {
  LIFETIME_FLOW_SERIES,
  LifetimeChartSeriesId,
  ORDERED_SERIES,
  STOCK_BALANCE_COLOR
} from "./constants";
import { useLifetimeChart } from "./hooks";
import { ProductLifetimeChartProps } from "./ProductLifetimeChart.types";
import { selectedDayPlugin } from "./selectedDay.plugin";

import styles from "./ProductLifetimeChart.module.scss";

// The generic <Chart> component doesn't auto-register controllers (unlike
// react-chartjs-2's typed <Bar>/<Line>), so the mixed bar+line chart needs
// both controllers registered explicitly.
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

type LegendMarker = "square" | "line" | "dashed";

interface LegendChip {
  id: LifetimeChartSeriesId;
  label: string;
  color: string;
  marker: LegendMarker;
  value: number;
}

const ProductLifetimeChart = ({
  data,
  breakdownBy,
  selectedIndex,
  onSelectIndex
}: ProductLifetimeChartProps) => {
  const [hiddenSeries, setHiddenSeries] = useState<LifetimeChartSeriesId[]>(
    []
  );

  const toggleSeries = (id: LifetimeChartSeriesId) =>
    setHiddenSeries((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );

  const { state, options } = useLifetimeChart({
    data,
    breakdownBy,
    selectedIndex,
    hiddenSeries,
    onSelectIndex
  });

  const getSeriesTotal = (key: (typeof LIFETIME_FLOW_SERIES)[number]["key"]) =>
    data.reduce((total, point) => total + point[key], 0);

  const chips: LegendChip[] = [
    {
      id: "stockBalance",
      label: "Stock balance",
      color: STOCK_BALANCE_COLOR,
      marker: "line",
      // the balance is a level, not a flow — show where it ended up
      value: data.length > 0 ? data[data.length - 1].stockBalance : 0
    },
    ...LIFETIME_FLOW_SERIES.map(
      (series): LegendChip => ({
        id: series.key,
        label: series.label,
        color: series.color,
        marker: "square",
        value: getSeriesTotal(series.key)
      })
    ),
    {
      id: ORDERED_SERIES.key,
      label: ORDERED_SERIES.label,
      color: ORDERED_SERIES.color,
      marker: "dashed",
      value: getSeriesTotal(ORDERED_SERIES.key)
    }
  ];

  return (
    <div className={styles.lifetimeChart}>
      <div className={styles.lifetimeChart_legend}>
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={cn(styles.lifetimeChart_chip, {
              [styles.lifetimeChart_chip__hidden]: hiddenSeries.includes(
                chip.id
              )
            })}
            onClick={() => toggleSeries(chip.id)}
          >
            <span
              className={cn(
                styles.lifetimeChart_marker,
                styles[`lifetimeChart_marker__${chip.marker}`]
              )}
              style={{ color: chip.color }}
            />
            <span className={styles.lifetimeChart_chipLabel}>
              {t(chip.label)}
            </span>
            <span className={styles.lifetimeChart_chipValue}>
              {chip.value}
            </span>
          </button>
        ))}
      </div>

      <div className={styles.lifetimeChart_canvas}>
        <Chart
          type="bar"
          data={state}
          options={options}
          plugins={[selectedDayPlugin]}
        />
      </div>
    </div>
  );
};

export default ProductLifetimeChart;
